import React, { useState, useEffect } from 'react';
import { Table, Button, message, Modal, Space, Tag, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import request from '../utils/request';
import type { DrawRecord } from '../types/loanDraw';

const LoanDrawManagement: React.FC = () => {
  const [draws, setDraws] = useState<DrawRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [status, setStatus] = useState<number | undefined>(undefined);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedDraw, setSelectedDraw] = useState<DrawRecord | null>(null);
  const [reviewStatus, setReviewStatus] = useState<number>(1);
  const [adminId, setAdminId] = useState<number>(1); // 这里应该从登录信息中获取

  const fetchDraws = async () => {
    setLoading(true);
    try {
      const response = await request.get('/loan-draw/list', {
        params: {
          page: pagination.current,
          pageSize: pagination.pageSize,
          status: status
        }
      });
      if (response.code === 200) {
        setDraws(response.data.list);
        setPagination({
          ...pagination,
          total: response.data.total
        });
      }
    } catch (error) {
      message.error('获取提款列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDraws();
  }, [pagination.current, pagination.pageSize, status]);

  const handleReview = async (draw: DrawRecord, status: number) => {
    setSelectedDraw(draw);
    setReviewStatus(status);
    setReviewModalVisible(true);
  };

  const handleReviewConfirm = async () => {
    if (!selectedDraw) return;

    try {
      const response = await request.post('/loan-draw/review', {
        id: selectedDraw.id,
        status: reviewStatus,
        adminId: adminId
      });

      if (response.code === 200) {
        message.success('审核成功');
        setReviewModalVisible(false);
        fetchDraws();
      } else {
        message.error(response.message || '审核失败');
      }
    } catch (error) {
      message.error('审核失败');
    }
  };

  const handleDelete = async (draw: DrawRecord) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条提款记录吗？',
      onOk: async () => {
        try {
          const response = await request.delete(`/loan-draw/${draw.id}`);
          if (response.code === 200) {
            message.success('删除成功');
            fetchDraws();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          message.error('删除失败');
        }
      }
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '钱包地址',
      dataIndex: 'wallet_address',
      key: 'wallet_address',
    },
    {
      title: '提款金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount} USDT`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => {
        const statusMap: Record<number, { text: string; color: string }> = {
          0: { text: '待审核', color: 'orange' },
          1: { text: '已通过', color: 'green' },
          2: { text: '已拒绝', color: 'red' }
        };
        return <Tag color={statusMap[status].color}>{statusMap[status].text}</Tag>;
      },
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: '审核时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
    },
    {
      title: '管理员',
      dataIndex: 'admin_name',
      key: 'admin_name',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: DrawRecord) => (
        <Space size="middle">
          {record.status === 0 && (
            <>
              <Button type="primary" onClick={() => handleReview(record, 1)}>
                通过
              </Button>
              <Button danger onClick={() => handleReview(record, 2)}>
                拒绝
              </Button>
            </>
          )}
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Select
          placeholder="选择状态"
          value={status}
          onChange={value => {
            setStatus(value);
            setPagination({ ...pagination, current: 1 });
          }}
          style={{ width: 120 }}
          allowClear
        >
          <Select.Option value={0}>待审核</Select.Option>
          <Select.Option value={1}>已通过</Select.Option>
          <Select.Option value={2}>已拒绝</Select.Option>
        </Select>
      </div>

      <Table
        columns={columns}
        dataSource={draws}
        rowKey="id"
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination({ ...pagination, current: page, pageSize });
          },
        }}
        loading={loading}
      />

      <Modal
        title="确认审核"
        open={reviewModalVisible}
        onOk={handleReviewConfirm}
        onCancel={() => setReviewModalVisible(false)}
      >
        <p>确定要{reviewStatus === 1 ? '通过' : '拒绝'}这笔提款申请吗？</p>
      </Modal>
    </div>
  );
};

export default LoanDrawManagement; 